import { cx } from '@linaria/core';
import { useId, useRef, useEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { pm2Transitions } from '@pm2/design-tokens';
import { pm2InterfaceStore } from '@pm2/store';
import { useUserIdentityState } from '@provider/user-identity';
import { resets } from '@ui/css';
import { makeBreakpoint } from '@utils/css';
import { useMatchBreakpoint, useScrollLocking, HideFor, CSSRefTransition } from '@utils/react';
import { ASIDE_TAKEOVER_BREAKPOINT_PX, wrapperCls, asideCls } from './styles/Aside.js';
export { selectors as asideSelectors } from './styles/Aside.js';
import { jsx } from 'react/jsx-runtime';

const EMIT_RELAYOUT_TRANSITION_COOLDOWN_MS = 250;
const LAST_OPENED_ASIDE_ID = {};
const LEFT_ASIDE_SELECTOR = "left-aside";
const RIGHT_ASIDE_SELECTOR = "right-aside";
function Aside({
  expanded,
  preventOverlap,
  hideFor,
  persistent,
  align = "left",
  onClose,
  children
}) {
  const leftAside = pm2InterfaceStore.useSelector(state => state.leftAside);
  const rightAside = pm2InterfaceStore.useSelector(state => state.rightAside);
  const asideId = useId();
  const isMountedRef = useRef(false);
  const user = useUserIdentityState(state => state.user);
  const notLoggedIn = user === null;
  const takenOverViewport = useMatchBreakpoint(makeBreakpoint(0, ASIDE_TAKEOVER_BREAKPOINT_PX));
  useScrollLocking(expanded === true && takenOverViewport === true);
  useEffect(() => {
    setTimeout(() => {
      isMountedRef.current = true;
    });
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // toggle aside classes on body layer when the aside is expanded / collapsed
  useEffect(() => {
    if (expanded) {
      LAST_OPENED_ASIDE_ID[align] = asideId;
      if (align === "left") {
        pm2InterfaceStore.dispatch(pm2InterfaceStore.actions.openLeftAside({
          id: asideId,
          preventOverlap: preventOverlap === true
        }));
      } else {
        pm2InterfaceStore.dispatch(pm2InterfaceStore.actions.openRightAside({
          id: asideId,
          preventOverlap: preventOverlap === true
        }));
      }
    } else {
      const isLastOpenedAside = align === "left" ? leftAside?.id === asideId : rightAside?.id === asideId;
      if (isLastOpenedAside) {
        // if this was the last opened aside in this side, then register that
        // asides in this direction have been collapsed

        // only disable class names if collapsing the last opened aside
        // (otherwise we might've just opened another one)
        document.body.classList.add(`aside--${align}-transitioning`);
        pm2InterfaceStore.dispatch(align === "left" ? pm2InterfaceStore.actions.closeLeftAside() : pm2InterfaceStore.actions.closeRightAside());

        // reset transitioning classes once the transition ends
        setTimeout(() => {
          document.body.classList.remove(`aside--${align}-transitioning`);
        }, pm2Transitions.asideCollapse.defaultDurationMs);
      }
    }
  }, [asideId, align, preventOverlap, expanded, leftAside?.id, rightAside?.id]);

  // auto-collapse the aside if another one is opened
  useEffect(() => {
    if (!expanded) {
      // bail out if already expanded
      return;
    }
    if (asideId === LAST_OPENED_ASIDE_ID[align]) {
      // bail out if this was the last opened aside (as it shouldn't be able
      // to collapse itself on opening)
      return;
    }
    if (align === "left") {
      if (leftAside && leftAside.id !== asideId) {
        // auto-collapse instantly if another aside was opened in this side
        // of the screen
        onClose();
      } else if (rightAside && rightAside.preventOverlap !== true && preventOverlap !== true) {
        // ... otherwise auto-collapse if an aside was opened in the other
        // side of the screen and this aside isn't configured to allow
        // overlapping
        onClose();
      }
    } else {
      // same rules as above, just for the other side of the viewport
      if (rightAside && rightAside.id !== asideId) {
        onClose();
      } else if (leftAside && leftAside.preventOverlap !== true && preventOverlap !== true) {
        onClose();
      }
    }
  },
  // intentionally disregard the onClose callback in the dependency hook, as
  // we only want to trigger the event once anyway
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [leftAside, rightAside, asideId, expanded, align, preventOverlap]);

  // synchronise changes in store.leftAside and store.rightAside so that we
  // ensure that collapses are correctly bubbled upwards
  useEffect(() => {
    const asideInStore = align === "left" ? leftAside : rightAside;
    if (asideInStore && asideInStore.id !== asideId && expanded) {
      onClose();
    }
  }, [asideId, expanded, align, leftAside, rightAside, onClose]);

  // In case the aside is forcefully closed, then ensure such update is
  // dispatched to our stores
  useEffect(() => {
    return () => {
      if (asideId === LAST_OPENED_ASIDE_ID[align]) {
        pm2InterfaceStore.dispatch(align === "left" ? pm2InterfaceStore.actions.closeLeftAside() : pm2InterfaceStore.actions.closeRightAside());
        onClose();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/jsx(HideFor, {
    breakpoint: hideFor,
    children: persistent ? /*#__PURE__*/jsx(CSSRefTransition, {
      in: expanded,
      classNames: wrapperCls,
      timeout: pm2Transitions.asideCollapse.defaultDurationMs,
      children: ref => /*#__PURE__*/jsx("div", {
        ref: ref,
        "aria-hidden": !expanded,
        className: cx(wrapperCls, resets.rootCls, align !== "right" && "align--left", align === "right" && "align--right", preventOverlap && "prevent-overlap", notLoggedIn && "notLoggedIn"),
        children: /*#__PURE__*/jsx("aside", {
          className: asideCls,
          "data-pm2-selector": align !== "right" ? LEFT_ASIDE_SELECTOR : RIGHT_ASIDE_SELECTOR,
          children: children
        })
      })
    }) : /*#__PURE__*/jsx(TransitionGroup, {
      component: null,
      appear: true,
      children: expanded && /*#__PURE__*/jsx(CSSRefTransition, {
        classNames: wrapperCls,
        timeout: pm2Transitions.asideCollapse.defaultDurationMs,
        children: ref => /*#__PURE__*/jsx("div", {
          ref: ref,
          className: cx(wrapperCls, resets.rootCls, align !== "right" && "align--left", align === "right" && "align--right", preventOverlap && "prevent-overlap", notLoggedIn && "notLoggedIn"),
          children: /*#__PURE__*/jsx("aside", {
            ref: ref,
            className: asideCls,
            "data-pm2-selector": align !== "right" ? LEFT_ASIDE_SELECTOR : RIGHT_ASIDE_SELECTOR,
            children: children
          })
        })
      })
    })
  });
}

export { Aside, EMIT_RELAYOUT_TRANSITION_COOLDOWN_MS, LEFT_ASIDE_SELECTOR, RIGHT_ASIDE_SELECTOR };
//# sourceMappingURL=Aside.js.map
